import { FormEvent, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ThemeToggle } from "../components/ThemeToggle";
import { BrandMark } from "./LoginPage";

/** First-user bootstrap. Renders when JWT_SECRET is set server-side but
 * no users exist yet. POST /auth/register is open exactly because
 * user_count == 0; subsequent registrations are 409'd. On success we
 * auto-login and land on /overview. */
export function SetupPage() {
  const { status, register, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (ready && status && !status.jwt_configured) {
    return <Navigate to="/auth/disabled" replace />;
  }
  if (ready && status && status.user_count > 0) {
    return <Navigate to="/login" replace />;
  }
  if (ready && status?.current_user != null) {
    return <Navigate to="/overview" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    const err = await register(email.trim(), password);
    setBusy(false);
    if (err) {
      setError(err);
      return;
    }
    navigate("/overview", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle compact />
      </div>

      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-7 shadow-card dark:shadow-card-dark">
        <div className="mb-6 flex flex-col items-center text-center">
          <BrandMark />
          <h1 className="mt-3 font-mono text-2xl font-bold tracking-tight text-fg">
            Set up FX-Dash
          </h1>
          <p className="mt-1 text-xs text-fg-muted">
            No users yet — create the admin account.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            id="email"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="username"
            autoFocus
          />
          <Field
            id="password"
            label="Password"
            hint="min 8 characters"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            minLength={8}
          />
          <Field
            id="confirm"
            label="Confirm password"
            type="password"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
          />
          {error && (
            <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-wait disabled:opacity-60"
          >
            {busy ? "creating account…" : "create admin account"}
          </button>
        </form>

        <p className="mt-5 text-center text-[10px] uppercase tracking-wider text-fg-muted">
          additional users via{" "}
          <code className="font-mono">python -m scripts.create_user</code>
        </p>
      </div>
    </div>
  );
}

// ---- helpers ---------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  autoFocus?: boolean;
  minLength?: number;
}

function Field({
  id,
  label,
  hint,
  type,
  value,
  onChange,
  autoComplete,
  autoFocus,
  minLength,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-[11px] font-medium uppercase tracking-wider text-fg-muted"
      >
        {label}
        {hint && (
          <span className="ml-2 normal-case text-fg-muted/70">({hint})</span>
        )}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        minLength={minLength}
        className="rounded-md border border-border bg-page px-3 py-2 font-mono text-sm text-fg outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}
